"use client";

import { useEffect, useRef, useState } from "react";

/**
 * SchoolERPAnimation
 *
 * Dependency-free looping SVG scene for the landing footer.
 *
 * A campus comes to life — bus pulls up, students walk in, windows light up,
 * the clock ticks, the flag waves — while the ERP layer radiates live data out
 * to floating product surfaces: an attendance roster ticking off students, an
 * analytics card whose bars grow, a fees chip and a library chip.
 *
 * Pure SVG + CSS keyframes: streams instantly and scales to any size. All
 * motion is disabled under `prefers-reduced-motion: reduce`.
 *
 * ONLY ANIMATES WHILE IT IS ON SCREEN.
 *
 * "Zero runtime cost" is what this file used to claim, and it isn't true. The
 * scene carries about fifty concurrent infinite animations, several of them on
 * groups painted through an `feDropShadow` filter, and two of them driven by
 * `offset-path` — which is to say a continuous re-raster of a filtered SVG
 * subtree, forever. CSS keyframes do not stop when they scroll out of view, so
 * on every marketing page this was burning frames in the footer the entire
 * time the visitor was reading the hero, which is most of the reason the site
 * felt heavy under a finger.
 *
 * So the whole scene is parked at `animation-play-state: paused` and only
 * released while an IntersectionObserver says it is actually visible. Pausing
 * rather than removing the animation keeps every element exactly where it was,
 * so scrolling back to the footer resumes the scene instead of snapping it to
 * a different frame.
 *
 * Without JS (or before hydration) the scene simply renders static, which is
 * the correct fallback for decoration.
 */

/* Curved data wires, shared between the rendered <path> and the packet
   `offset-path` so a packet always rides exactly on its wire. */
const WIRES = {
  roster: "M200,96 Q150,84 106,114",
  chart: "M200,96 Q250,84 294,114",
  fees: "M200,96 Q92,80 52,153",
  library: "M200,96 Q308,80 348,153",
  cloud: "M200,96 Q176,66 150,60",
};

const css = `
.ks-scene { display:block; width:100%; height:100%; overflow:visible; }
.ks-scene * { transform-box: fill-box; }

/* Default is paused; \`.ks-live\` (set by the IntersectionObserver below)
   is what lets the scene run. Written this way round on purpose — the
   pre-hydration and no-JS states are then both "static", never "animating
   forever off-screen". */
.ks-scene * { animation-play-state: paused }
.ks-scene.ks-live * { animation-play-state: running }

/* ---------------- ambient ---------------- */
@keyframes ks-drift  { from { transform: translateX(-90px) } to { transform: translateX(480px) } }
@keyframes ks-breathe{ 0%,100% { transform: translateY(0) scale(1) } 50% { transform: translateY(-2px) scale(1.012) } }
@keyframes ks-sway   { 0%,100% { transform: rotate(-2.5deg) } 50% { transform: rotate(2.5deg) } }
@keyframes ks-plot   { 0%,100% { transform: scaleX(1); opacity:.85 } 50% { transform: scaleX(.97); opacity:.7 } }
@keyframes ks-twinkle{ 0%,100% { transform: scale(.4); opacity:0 } 50% { transform: scale(1); opacity:.9 } }
@keyframes ks-flap   { 0%,100% { transform: scaleY(1) } 50% { transform: scaleY(.45) } }

.ks-cloud-a { animation: ks-drift 38s linear infinite }
.ks-cloud-b { animation: ks-drift 54s linear infinite; animation-delay:-22s }
.ks-campus  { animation: ks-breathe 9s ease-in-out infinite; transform-origin:50% 100% }
.ks-tree    { animation: ks-sway 5.5s ease-in-out infinite; transform-origin:50% 100% }
.ks-tree-b  { animation-delay:-2.4s; animation-duration:6.6s }
.ks-tree-c  { animation-delay:-1.2s; animation-duration:4.9s }
.ks-plot    { animation: ks-plot 9s ease-in-out infinite; transform-origin:50% 50% }
.ks-star    { animation: ks-twinkle 3.4s ease-in-out infinite; transform-origin:50% 50% }
.ks-star-2  { animation-delay:-1.1s }
.ks-star-3  { animation-delay:-2.3s }
.ks-bird    { animation: ks-drift 26s linear infinite }
.ks-bird-b  { animation-delay:-11s; animation-duration:33s }
.ks-wing    { animation: ks-flap .5s ease-in-out infinite; transform-origin:50% 100% }

/* ---------------- flag ---------------- */
@keyframes ks-flag { 0%,100% { transform: skewY(0deg) scaleX(1) } 34% { transform: skewY(-8deg) scaleX(.92) } 68% { transform: skewY(7deg) scaleX(1) } }
.ks-flag { animation: ks-flag 2.2s ease-in-out infinite; transform-origin:0% 50% }

/* ---------------- clock ---------------- */
@keyframes ks-spin { to { transform: rotate(360deg) } }
.ks-hand-min  { animation: ks-spin 6s linear infinite;  transform-origin:50% 100% }
.ks-hand-hour { animation: ks-spin 36s linear infinite; transform-origin:50% 100% }

/* ---------------- windows lighting up ---------------- */
@keyframes ks-lamp { 0%,10% { opacity:0 } 20%,70% { opacity:1 } 82%,100% { opacity:0 } }
.ks-lamp { opacity:0; animation: ks-lamp 8s ease-in-out infinite }
.ks-lamp:nth-of-type(6n+1) { animation-delay:-.3s }
.ks-lamp:nth-of-type(6n+2) { animation-delay:-1.6s }
.ks-lamp:nth-of-type(6n+3) { animation-delay:-2.9s }
.ks-lamp:nth-of-type(6n+4) { animation-delay:-4.2s }
.ks-lamp:nth-of-type(6n+5) { animation-delay:-5.5s }
.ks-lamp:nth-of-type(6n+6) { animation-delay:-6.8s }

/* ---------------- school bus ---------------- */
@keyframes ks-bus {
  0%   { transform: translateX(-120px) }
  24%  { transform: translateX(146px) }
  46%  { transform: translateX(146px) }   /* halted at the gate */
  76%  { transform: translateX(440px) }
  100% { transform: translateX(440px) }
}
@keyframes ks-wheel {
  0% { transform: rotate(0) } 24% { transform: rotate(760deg) }
  46% { transform: rotate(760deg) } 76% { transform: rotate(1560deg) } 100% { transform: rotate(1560deg) }
}
@keyframes ks-jounce { 0%,100% { transform: translateY(0) } 50% { transform: translateY(.7px) } }
@keyframes ks-streak { 0%,20% { opacity:.55 } 26%,74% { opacity:0 } 80%,100% { opacity:.55 } }
.ks-bus    { animation: ks-bus 14s ease-in-out infinite }
.ks-chassis{ animation: ks-jounce .42s ease-in-out infinite }
.ks-wheel  { animation: ks-wheel 14s ease-in-out infinite; transform-origin:50% 50% }
.ks-streak { animation: ks-streak 14s ease-in-out infinite }

/* ---------------- students ---------------- */
@keyframes ks-walk {
  0%,30%  { transform: translate(0,0); opacity:0 }
  36%     { opacity:1 }
  72%     { transform: translate(70px,-10px); opacity:1 }
  82%,100%{ transform: translate(80px,-12px); opacity:0 }
}
@keyframes ks-step { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-1.4px) } }
.ks-kid   { animation: ks-walk 14s linear infinite }
.ks-kid-b { animation-delay:-1.3s }
.ks-hop   { animation: ks-step .42s ease-in-out infinite }

/* ---------------- ERP data flow ---------------- */
@keyframes ks-dash { to { stroke-dashoffset:-30 } }
.ks-wire { stroke-dasharray:3 5; animation: ks-dash 1.7s linear infinite }

@keyframes ks-ride { 0% { offset-distance:0%; opacity:0 } 12% { opacity:1 } 86% { opacity:1 } 100% { offset-distance:100%; opacity:0 } }
.ks-pkt { offset-rotate:0deg; animation: ks-ride 2.8s linear infinite }
.ks-pkt-roster  { offset-path: path('${WIRES.roster}') }
.ks-pkt-chart   { offset-path: path('${WIRES.chart}');   animation-delay:-.56s }
.ks-pkt-fees    { offset-path: path('${WIRES.fees}');    animation-delay:-1.12s }
.ks-pkt-library { offset-path: path('${WIRES.library}'); animation-delay:-1.68s }
.ks-pkt-cloud   { offset-path: path('${WIRES.cloud}');   animation-delay:-2.24s }
/* without offset-path the packets would pile up at the origin — hide them, the
   flowing dashed wires still read as movement */
@supports not (offset-path: path('M0,0 L1,1')) { .ks-pkt { display:none } }

@keyframes ks-hub { 0%,100% { transform:scale(.7); opacity:.45 } 50% { transform:scale(1.25); opacity:.08 } }
.ks-hub { animation: ks-hub 2.8s ease-in-out infinite; transform-origin:50% 50% }

/* ---------------- floating surfaces ---------------- */
@keyframes ks-float { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-5px) } }
.ks-float   { animation: ks-float 4.6s ease-in-out infinite }
.ks-float-2 { animation-delay:-1.15s; animation-duration:5.2s }
.ks-float-3 { animation-delay:-2.3s;  animation-duration:4.1s }
.ks-float-4 { animation-delay:-3.45s; animation-duration:5.6s }

/* roster check marks popping in */
@keyframes ks-tick { 0%,4% { opacity:0; transform:scale(.3) } 14% { opacity:1; transform:scale(1.25) } 22%,84% { opacity:1; transform:scale(1) } 94%,100% { opacity:0; transform:scale(.6) } }
.ks-tick { opacity:0; animation: ks-tick 4.2s ease-in-out infinite; transform-origin:50% 50% }
.ks-tick-2 { animation-delay:.5s }
.ks-tick-3 { animation-delay:1s }

/* analytics bars growing */
@keyframes ks-bar { 0% { transform:scaleY(.12) } 42% { transform:scaleY(1) } 72% { transform:scaleY(.66) } 100% { transform:scaleY(.12) } }
.ks-bar { animation: ks-bar 4.2s ease-in-out infinite; transform-origin:50% 100% }
.ks-bar-2 { animation-delay:-.35s } .ks-bar-3 { animation-delay:-.7s } .ks-bar-4 { animation-delay:-1.05s }

@keyframes ks-draw { 0% { stroke-dashoffset:60 } 40%,80% { stroke-dashoffset:0 } 100% { stroke-dashoffset:60 } }
.ks-trend { stroke-dasharray:60; animation: ks-draw 4.2s ease-in-out infinite }

/* fee coin flip + book page turn */
@keyframes ks-flip { 0%,60% { transform:rotateY(0) } 75% { transform:rotateY(180deg) } 90%,100% { transform:rotateY(360deg) } }
.ks-flip { animation: ks-flip 4.2s ease-in-out infinite; transform-origin:50% 50% }
@keyframes ks-page { 0%,55% { transform:scaleX(1) } 70% { transform:scaleX(-.15) } 85%,100% { transform:scaleX(1) } }
.ks-page { animation: ks-page 4.2s ease-in-out infinite; transform-origin:0% 50% }

/* ---------------- cloud sync ---------------- */
@keyframes ks-ring { 0% { transform:scale(.5); opacity:.45 } 100% { transform:scale(1.55); opacity:0 } }
@keyframes ks-up   { 0%,50%,100% { transform:translateY(0); opacity:0 } 12% { opacity:1 } 40% { transform:translateY(-7px); opacity:0 } }
@keyframes ks-down { 0%,50%,100% { transform:translateY(0); opacity:0 } 62% { opacity:1 } 90% { transform:translateY(7px); opacity:0 } }
.ks-ring   { animation: ks-ring 3s ease-out infinite; transform-origin:50% 50% }
.ks-ring-2 { animation-delay:-1.5s }
.ks-arrow-up   { animation: ks-up 3s ease-in-out infinite }
.ks-arrow-down { animation: ks-down 3s ease-in-out infinite }

@media (prefers-reduced-motion: reduce) {
  .ks-scene *, .ks-scene *::before, .ks-scene *::after { animation: none !important }
  .ks-lamp, .ks-tick { opacity:1 }
  .ks-bar { transform: scaleY(.8) }
  .ks-trend { stroke-dashoffset:0 }
}
`;

/** Arched (rounded-top) window path. */
const arch = (x: number, y: number, w: number, h: number) =>
  `M${x},${y + h} L${x},${y + w / 2} A${w / 2},${w / 2} 0 0 1 ${x + w},${y + w / 2} L${x + w},${y + h} Z`;

const MAIN_WINDOWS: Array<[number, number]> = [
  [126, 154],
  [150, 154],
  [232, 154],
  [256, 154],
  [126, 192],
  [150, 192],
  [232, 192],
  [256, 192],
];

const WING_WINDOWS: Array<[number, number]> = [
  [94, 180],
  [94, 206],
  [290, 180],
  [290, 206],
];

export default function SchoolERPAnimation({ className = "" }: { className?: string }) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [live, setLive] = useState(false);

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return undefined;

    // No IntersectionObserver (very old Safari) — fall back to running it, the
    // pre-existing behaviour, rather than to a scene that never moves.
    if (typeof IntersectionObserver === "undefined") {
      setLive(true);
      return undefined;
    }

    // No `once`: this has to toggle both ways, because the point is to stop
    // the animation again when the footer scrolls back off.
    const obs = new IntersectionObserver(
      ([entry]) => setLive(entry.isIntersecting),
      { rootMargin: "100px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div className={className}>
      {/* viewBox is cropped tight to the drawn content (28,26 → 372,284) so the
          scene fills its slot instead of letterboxing inside dead margin.
          Ratio is exactly 4:3 — keep the container on aspect-[4/3]. */}
      <svg
        ref={svgRef}
        viewBox="28 26 344 258"
        className={`ks-scene${live ? " ks-live" : ""}`}
        role="img"
        aria-label="Animated illustration of a school campus streaming live attendance, fees, exam and analytics data through Kaympus"
      >
        <style dangerouslySetInnerHTML={{ __html: css }} />

        <defs>
          <linearGradient id="ks-wall" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FFFFFF" />
            <stop offset="100%" stopColor="#E7EBFB" />
          </linearGradient>
          <linearGradient id="ks-wall2" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FCFCFE" />
            <stop offset="100%" stopColor="#E0E5FA" />
          </linearGradient>
          <linearGradient id="ks-roof" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#4F6BE4" />
            <stop offset="100%" stopColor="#1B35AC" />
          </linearGradient>
          <linearGradient id="ks-plot" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#DFE4FA" />
            <stop offset="100%" stopColor="#CFD6F7" />
          </linearGradient>
          <linearGradient id="ks-road" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#D4DBF8" stopOpacity="0" />
            <stop offset="14%" stopColor="#D4DBF8" stopOpacity="1" />
            <stop offset="86%" stopColor="#D4DBF8" stopOpacity="1" />
            <stop offset="100%" stopColor="#D4DBF8" stopOpacity="0" />
          </linearGradient>
          <radialGradient id="ks-halo" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#3D5EE1" stopOpacity="0.16" />
            <stop offset="100%" stopColor="#3D5EE1" stopOpacity="0" />
          </radialGradient>
          <filter id="ks-lift" x="-40%" y="-40%" width="180%" height="180%">
            <feDropShadow dx="0" dy="3" stdDeviation="3.5" floodColor="#1B35AC" floodOpacity="0.16" />
          </filter>
          <filter id="ks-soft" x="-40%" y="-40%" width="180%" height="180%">
            <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#1B35AC" floodOpacity="0.12" />
          </filter>
        </defs>

        {/* ambient halo */}
        <ellipse cx="200" cy="150" rx="175" ry="128" fill="url(#ks-halo)" />

        {/* drifting clouds + birds */}
        <g>
          <g className="ks-cloud-a" fill="#FFFFFF" opacity="0.85">
            <path d="M0 30a6 6 0 0 1 6-6 8 8 0 0 1 15-2 7 7 0 0 1 7 8 5 5 0 0 1-5 5H5a5 5 0 0 1-5-5z" />
          </g>
          <g className="ks-cloud-b" fill="#FFFFFF" opacity="0.55">
            <path d="M40 76a5 5 0 0 1 5-5 6 6 0 0 1 12-1 5 5 0 0 1 5 6 4 4 0 0 1-4 4H44a4 4 0 0 1-4-4z" />
          </g>
          <g className="ks-bird" stroke="#A8B5F1" strokeWidth="1.3" strokeLinecap="round" fill="none">
            <g className="ks-wing">
              <path d="M0 18 4 14 8 18" />
            </g>
          </g>
          <g className="ks-bird ks-bird-b" stroke="#A8B5F1" strokeWidth="1.1" strokeLinecap="round" fill="none" opacity="0.7">
            <g className="ks-wing" style={{ animationDelay: "-0.18s" }}>
              <path d="M18 30 21 27 24 30" />
            </g>
          </g>
        </g>

        {/* twinkling accents */}
        <g fill="#4864E2">
          <path className="ks-star" d="M118 74l1.6 4 4 1.6-4 1.6-1.6 4-1.6-4-4-1.6 4-1.6z" />
          <path className="ks-star ks-star-2" d="M276 66l1.4 3.4 3.4 1.4-3.4 1.4-1.4 3.4-1.4-3.4-3.4-1.4 3.4-1.4z" />
          <path className="ks-star ks-star-3" d="M84 132l1.2 3 3 1.2-3 1.2-1.2 3-1.2-3-3-1.2 3-1.2z" />
        </g>

        {/* ================= data wires (behind the campus) ================= */}
        <g fill="none" stroke="#9EADF0" strokeWidth="1.2" strokeLinecap="round" opacity="0.6">
          {Object.entries(WIRES).map(([k, d]) => (
            <path key={k} className="ks-wire" d={d} />
          ))}
        </g>
        <circle className="ks-hub" cx="200" cy="96" r="13" fill="#3D5EE1" />
        <g>
          <circle className="ks-pkt ks-pkt-roster" r="2.8" fill="#2040CF" />
          <circle className="ks-pkt ks-pkt-chart" r="2.8" fill="#4864E2" />
          <circle className="ks-pkt ks-pkt-fees" r="2.8" fill="#2040CF" />
          <circle className="ks-pkt ks-pkt-library" r="2.8" fill="#4864E2" />
          <circle className="ks-pkt ks-pkt-cloud" r="2.4" fill="#2040CF" />
        </g>

        {/* ================= cloud sync badge ================= */}
        <g transform="translate(132 58)" className="ks-float ks-float-4">
          <circle className="ks-ring" cx="0" cy="0" r="19" fill="none" stroke="#3D5EE1" strokeWidth="1.3" />
          <circle className="ks-ring ks-ring-2" cx="0" cy="0" r="19" fill="none" stroke="#3D5EE1" strokeWidth="1.3" />
          <path
            d="M-17 3a6 6 0 0 1 6-6 8 8 0 0 1 15-2 7 7 0 0 1 7 8 5 5 0 0 1-5 5h-17a6 6 0 0 1-6-5z"
            fill="#FFFFFF"
            stroke="#C1CAF5"
            strokeWidth="1.2"
            filter="url(#ks-soft)"
          />
          <g stroke="#2040CF" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none">
            <g className="ks-arrow-up">
              <path d="M-4 5V-1M-6.6 1.6-4-1-1.4 1.6" />
            </g>
            <g className="ks-arrow-down">
              <path d="M5-1v6M2.4 2.4 5 5 7.6 2.4" />
            </g>
          </g>
        </g>

        {/* ================= attendance roster card ================= */}
        <g className="ks-float">
          <rect x="34" y="92" width="72" height="50" rx="9" fill="#FFFFFF" stroke="#D0D7F8" strokeWidth="1.2" filter="url(#ks-lift)" />
          <rect x="42" y="100" width="26" height="4" rx="2" fill="#2040CF" opacity="0.75" />
          <circle cx="98" cy="102" r="2.4" fill="#4864E2" />
          {[0, 1, 2].map((i) => (
            <g key={i}>
              <circle cx="45" cy={114 + i * 10} r="3.4" fill="#E0E5FA" stroke="#CAD3F7" strokeWidth="0.9" />
              <rect x="53" y={112.2 + i * 10} width={32 - i * 5} height="3.6" rx="1.8" fill="#E4E8FB" />
              <path
                className={`ks-tick${i ? ` ks-tick-${i + 1}` : ""}`}
                d={`M92 ${114 + i * 10}l2.4 2.6 4.6-5.4`}
                fill="none"
                stroke="#4864E2"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
          ))}
        </g>

        {/* ================= analytics card ================= */}
        <g className="ks-float ks-float-2">
          <rect x="294" y="92" width="72" height="52" rx="9" fill="#FFFFFF" stroke="#D0D7F8" strokeWidth="1.2" filter="url(#ks-lift)" />
          <rect x="302" y="100" width="24" height="4" rx="2" fill="#2040CF" opacity="0.75" />
          <path className="ks-trend" d="M302 122l12-8 12 5 14-13" fill="none" stroke="#A8B5F1" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          <line x1="302" y1="136" x2="358" y2="136" stroke="#E0E5FA" strokeWidth="1.2" strokeLinecap="round" />
          {[
            { x: 303, h: 18, c: "#4864E2", cls: "" },
            { x: 316, h: 24, c: "#3D5EE1", cls: " ks-bar-2" },
            { x: 329, h: 14, c: "#8D9EED", cls: " ks-bar-3" },
            { x: 342, h: 22, c: "#2040CF", cls: " ks-bar-4" },
          ].map((b) => (
            <rect key={b.x} className={`ks-bar${b.cls}`} x={b.x} y={135 - b.h} width="9" height={b.h} rx="2.5" fill={b.c} />
          ))}
        </g>

        {/* ================= fees chip ================= */}
        <g className="ks-float ks-float-3">
          <circle cx="52" cy="170" r="17" fill="#FFFFFF" stroke="#D0D7F8" strokeWidth="1.2" filter="url(#ks-soft)" />
          <g className="ks-flip">
            <circle cx="52" cy="170" r="10.5" fill="#ECEFFC" stroke="#BDC7F5" strokeWidth="1" />
            <text
              x="52"
              y="175"
              textAnchor="middle"
              fontSize="13"
              fontWeight="700"
              fill="#2040CF"
              fontFamily="system-ui, -apple-system, Segoe UI, sans-serif"
            >
              ₹
            </text>
          </g>
        </g>

        {/* ================= library chip ================= */}
        <g className="ks-float ks-float-4">
          <circle cx="348" cy="170" r="17" fill="#FFFFFF" stroke="#D0D7F8" strokeWidth="1.2" filter="url(#ks-soft)" />
          <g fill="none" stroke="#2040CF" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M348 164v11" />
            <path d="M348 164a7 7 0 0 0-7-2v11a7 7 0 0 1 7 2" />
            <g className="ks-page" style={{ transformOrigin: "348px 170px" }}>
              <path d="M348 164a7 7 0 0 1 7-2v11a7 7 0 0 0-7 2" />
            </g>
          </g>
        </g>

        {/* ================= ground plot ================= */}
        <g className="ks-plot">
          <ellipse cx="200" cy="226" rx="160" ry="31" fill="url(#ks-plot)" />
        </g>
        <ellipse cx="200" cy="234" rx="122" ry="9" fill="#1B35AC" opacity="0.07" />

        {/* walkway from the steps down to the road */}
        <path d="M186 240 L172 258 L228 258 L214 240 Z" fill="#E5E9FB" opacity="0.9" />

        {/* ================= campus ================= */}
        <g className="ks-campus">
          {/* side wings */}
          <g stroke="#CAD3F7" strokeWidth="1.3">
            <rect x="88" y="170" width="30" height="66" rx="4" fill="url(#ks-wall2)" />
            <rect x="282" y="170" width="30" height="66" rx="4" fill="url(#ks-wall2)" />
          </g>
          <rect x="85" y="163" width="36" height="9" rx="3" fill="url(#ks-roof)" />
          <rect x="279" y="163" width="36" height="9" rx="3" fill="url(#ks-roof)" />

          {/* main block */}
          <rect x="118" y="140" width="164" height="96" rx="4" fill="url(#ks-wall)" stroke="#CAD3F7" strokeWidth="1.3" />
          <rect x="112" y="131" width="176" height="11" rx="4" fill="url(#ks-roof)" />
          <rect x="118" y="182" width="164" height="2.5" fill="#3D5EE1" opacity="0.12" />

          {/* clock tower */}
          <rect x="186" y="104" width="28" height="38" rx="3" fill="url(#ks-wall2)" stroke="#CAD3F7" strokeWidth="1.3" />
          <path d="M179 105 200 83l21 22z" fill="url(#ks-roof)" />
          <circle cx="200" cy="122" r="8.5" fill="#FFFFFF" stroke="#2040CF" strokeWidth="1.5" />
          <circle cx="200" cy="122" r="5.6" fill="none" stroke="#D0D7F8" strokeWidth="0.8" />
          <g stroke="#2040CF" strokeWidth="1.4" strokeLinecap="round">
            <line className="ks-hand-hour" x1="200" y1="117" x2="200" y2="122" />
            <line className="ks-hand-min" x1="200" y1="115" x2="200" y2="122" />
          </g>

          {/* flag */}
          <line x1="200" y1="83" x2="200" y2="58" stroke="#2040CF" strokeWidth="1.5" strokeLinecap="round" />
          <path className="ks-flag" d="M200 60h20l-5 5 5 5h-20z" fill="#3D5EE1" />

          {/* windows */}
          {MAIN_WINDOWS.map(([x, y]) => (
            <path key={`w${x}-${y}`} d={arch(x, y, 18, 24)} fill="#E0E5FA" stroke="#CAD3F7" strokeWidth="1.1" />
          ))}
          {MAIN_WINDOWS.map(([x, y]) => (
            <path key={`l${x}-${y}`} className="ks-lamp" d={arch(x, y, 18, 24)} fill="#FFC773" />
          ))}
          {WING_WINDOWS.map(([x, y]) => (
            <path key={`ww${x}-${y}`} d={arch(x, y, 16, 20)} fill="#E0E5FA" stroke="#CAD3F7" strokeWidth="1.1" />
          ))}
          {WING_WINDOWS.map(([x, y]) => (
            <path key={`wl${x}-${y}`} className="ks-lamp" d={arch(x, y, 16, 20)} fill="#FFC773" />
          ))}

          {/* portico: pediment, entablature, columns, arched door */}
          <path d="M166 155 200 132l34 23z" fill="url(#ks-roof)" />
          <rect x="168" y="154" width="64" height="9" rx="2.5" fill="#2040CF" />
          <rect x="174" y="163" width="6" height="68" rx="3" fill="#F5F6FD" stroke="#CAD3F7" strokeWidth="1" />
          <rect x="184" y="163" width="6" height="68" rx="3" fill="#F5F6FD" stroke="#CAD3F7" strokeWidth="1" />
          <rect x="210" y="163" width="6" height="68" rx="3" fill="#F5F6FD" stroke="#CAD3F7" strokeWidth="1" />
          <rect x="220" y="163" width="6" height="68" rx="3" fill="#F5F6FD" stroke="#CAD3F7" strokeWidth="1" />
          <path d="M194 236v-32a6 6 0 0 1 12 0v32z" fill="#1B35AC" opacity="0.9" />
          <line x1="200" y1="200" x2="200" y2="236" stroke="#E0E5FA" strokeWidth="1" opacity="0.55" />

          {/* steps */}
          <rect x="170" y="236" width="60" height="4" rx="2" fill="#D1D8F8" />
          <rect x="164" y="240" width="72" height="4" rx="2" fill="#C5CEF6" />
        </g>

        {/* ================= landscaping ================= */}
        <g>
          <g className="ks-tree">
            <rect x="62" y="206" width="4" height="22" rx="2" fill="#8C6A4F" />
            <circle cx="64" cy="200" r="16" fill="#8D9EED" />
            <circle cx="55" cy="208" r="10" fill="#6981E8" />
            <circle cx="72" cy="209" r="8" fill="#5C75E6" />
          </g>
          <g className="ks-tree ks-tree-b">
            <rect x="334" y="206" width="4" height="22" rx="2" fill="#8C6A4F" />
            <circle cx="336" cy="200" r="15" fill="#8D9EED" />
            <circle cx="345" cy="208" r="9" fill="#6981E8" />
            <circle cx="328" cy="209" r="8" fill="#5C75E6" />
          </g>
          <g className="ks-tree ks-tree-c">
            <rect x="127" y="216" width="3" height="14" rx="1.5" fill="#8C6A4F" />
            <circle cx="128.5" cy="212" r="9" fill="#8D9EED" />
          </g>
          <g className="ks-tree ks-tree-c" style={{ animationDelay: "-3.1s" }}>
            <rect x="270" y="216" width="3" height="14" rx="1.5" fill="#8C6A4F" />
            <circle cx="271.5" cy="212" r="8" fill="#8D9EED" />
          </g>
          {/* hedges */}
          <g fill="#6981E8" opacity="0.5">
            <ellipse cx="148" cy="234" rx="13" ry="5" />
            <ellipse cx="252" cy="234" rx="13" ry="5" />
            <ellipse cx="100" cy="238" rx="10" ry="4" />
            <ellipse cx="300" cy="238" rx="10" ry="4" />
          </g>
        </g>

        {/* ================= students walking in ================= */}
        <g>
          <g className="ks-kid" transform="translate(132 252)">
            <g className="ks-hop">
              <circle cx="0" cy="-9" r="3.2" fill="#1B35AC" />
              <path d="M-3.4 0v-4.4a3.4 3.4 0 0 1 6.8 0V0z" fill="#3D5EE1" />
              <path d="M-2 0v4M2 0v4" stroke="#1B35AC" strokeWidth="1.4" strokeLinecap="round" />
            </g>
          </g>
          <g className="ks-kid ks-kid-b" transform="translate(146 256)">
            <g className="ks-hop" style={{ animationDelay: "-0.21s" }}>
              <circle cx="0" cy="-9" r="3.2" fill="#E09B23" />
              <path d="M-3.4 0v-4.4a3.4 3.4 0 0 1 6.8 0V0z" fill="#FFC773" />
              <path d="M-2 0v4M2 0v4" stroke="#B7791F" strokeWidth="1.4" strokeLinecap="round" />
            </g>
          </g>
        </g>

        {/* ================= road + school bus ================= */}
        <rect x="0" y="256" width="400" height="20" rx="10" fill="url(#ks-road)" />
        <line x1="30" y1="266" x2="370" y2="266" stroke="#FFFFFF" strokeWidth="2" strokeDasharray="11 13" strokeLinecap="round" opacity="0.85" />

        <g className="ks-bus">
          <ellipse cx="32" cy="274" rx="32" ry="4" fill="#1B35AC" opacity="0.12" />
          <g className="ks-streak" stroke="#A8B5F1" strokeWidth="1.6" strokeLinecap="round">
            <line x1="-16" y1="242" x2="-4" y2="242" />
            <line x1="-22" y1="250" x2="-6" y2="250" />
            <line x1="-14" y1="258" x2="-4" y2="258" />
          </g>
          <g className="ks-chassis">
            <rect x="0" y="234" width="66" height="28" rx="8" fill="#F5B33C" />
            <path d="M8 234h50a8 8 0 0 1 8 8v-1a8 8 0 0 0-8-7z" fill="#FFD07A" />
            <rect x="0" y="248" width="66" height="4" fill="#1B35AC" opacity="0.85" />
            <rect x="6" y="238" width="13" height="9" rx="2.5" fill="#E0E5FA" />
            <rect x="23" y="238" width="13" height="9" rx="2.5" fill="#E0E5FA" />
            <rect x="40" y="238" width="19" height="9" rx="2.5" fill="#E0E5FA" />
            <rect x="4" y="254" width="10" height="4" rx="2" fill="#2040CF" opacity="0.5" />
            <circle cx="62" cy="257" r="2.2" fill="#FFF3D6" />
            <rect x="64" y="240" width="3" height="5" rx="1.5" fill="#1B35AC" opacity="0.6" />
          </g>
          <g transform="translate(16 264)">
            <g className="ks-wheel">
              <circle cx="0" cy="0" r="6" fill="#2F3E46" />
              <circle cx="0" cy="0" r="2.4" fill="#E0E5FA" />
              <line x1="0" y1="-6" x2="0" y2="6" stroke="#E0E5FA" strokeWidth="1" />
              <line x1="-6" y1="0" x2="6" y2="0" stroke="#E0E5FA" strokeWidth="1" />
            </g>
          </g>
          <g transform="translate(51 264)">
            <g className="ks-wheel">
              <circle cx="0" cy="0" r="6" fill="#2F3E46" />
              <circle cx="0" cy="0" r="2.4" fill="#E0E5FA" />
              <line x1="0" y1="-6" x2="0" y2="6" stroke="#E0E5FA" strokeWidth="1" />
              <line x1="-6" y1="0" x2="6" y2="0" stroke="#E0E5FA" strokeWidth="1" />
            </g>
          </g>
        </g>
      </svg>
    </div>
  );
}

